import type { TrackPageviewRequest } from "@/types";
import { Logger } from "@/core/logger";

let scrollDepth = 0;
let pageLoadTime = 0;
let isTrackingEngagement = false;

export function getPageUrl(): string {
  if (typeof window === "undefined" || !window.location) {
    Logger.warn("getPageUrl: window.location unavailable");
    return "";
  }
  return window.location.href;
}

export function getPageTitle(): string {
  if (typeof document === "undefined") {
    Logger.warn("getPageTitle: document unavailable");
    return "";
  }
  return document.title || "";
}

export function getReferrer(): string {
  if (typeof document === "undefined") {
    Logger.warn("getReferrer: document unavailable");
    return "";
  }
  return document.referrer || "";
}

export function getPageType(): string {
  if (typeof window === "undefined") return "";
  const pathname = window.location.pathname.toLowerCase();
  if (pathname === "/" || pathname === "") return "homepage";
  if (pathname.includes("/product")) return "product";
  if (pathname.includes("/checkout")) return "checkout";
  if (pathname.includes("/cart")) return "cart";
  return "other";
}

export function getTimestamp(): string {
  return new Date().toISOString();
}

export function getUserIp(): string {
  // Server-side extraction only
  // Backend will populate from: req.ip || req.headers['x-forwarded-for']
  return "";
}

export function getPageviewId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "";
}

export function getTrafficSource(): string {
  if (typeof window === "undefined") return "";

  // 1. Check UTM parameters first (highest priority)
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get("utm_source");
  if (utmSource) return utmSource;

  // 2. Check referrer
  if (typeof document === "undefined") return "";

  const referrer = document.referrer;
  if (!referrer) return "direct";

  try {
    const referrerUrl = new URL(referrer);
    const domain = referrerUrl.hostname.toLowerCase();
    const currentDomain = window.location.hostname.toLowerCase();

    // Same domain = internal navigation
    if (domain === currentDomain) return "internal";

    // Search engines
    if (domain.includes("google")) return "organic_search";
    if (domain.includes("bing")) return "organic_search";
    if (domain.includes("yahoo")) return "organic_search";
    if (domain.includes("duckduckgo")) return "organic_search";

    // Social media
    if (domain.includes("facebook")) return "social";
    if (domain.includes("instagram")) return "social";
    if (domain.includes("twitter")) return "social";
    if (domain.includes("linkedin")) return "social";
    if (domain.includes("youtube")) return "social";
    if (domain.includes("tiktok")) return "social";

    // Email
    if (domain.includes("mail")) return "email";
    if (domain.includes("gmail")) return "email";

    // Other referral
    return "referral";
  } catch {
    return "unknown";
  }
}

export function getDeviceCategory(): string {
  if (typeof navigator === "undefined" || !navigator.userAgent) {
    Logger.warn("getDeviceCategory: navigator.userAgent unavailable, defaulting to desktop");
    return "desktop";
  }

  const ua = navigator.userAgent.toLowerCase();

  // Check for tablets first (more specific pattern)
  // iPad, Android tablets, Kindle Fire, etc.
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }

  // Check for mobile devices
  // iPhone, Android phones, etc.
  if (/mobile|android|iphone|ipod|blackberry|opera mini|iemobile/i.test(ua)) {
    return "mobile";
  }

  // Default to desktop
  return "desktop";
}

function isValidContentId(id: string): boolean {
  if (!id || typeof id !== "string") return false;
  if (id.length > 100) return false; // Max length check
  if (id.length < 1) return false;

  // Only allow: letters, numbers, dash, underscore
  return /^[a-zA-Z0-9_-]+$/.test(id);
}

function sanitizeContentId(id: string): string {
  // Remove any character that's not alphanumeric, dash, or underscore
  return id.replace(/[^a-zA-Z0-9_-]/g, "").substring(0, 100);
}

export function getContentId(): string {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return "";
  }

  try {
    // 1. Check URL parameters (safest - user can't inject into URL structure)
    const params = new URLSearchParams(window.location.search);
    const urlId = params.get("product_id") || params.get("id") || params.get("item_id");
    if (urlId && isValidContentId(urlId)) {
      return sanitizeContentId(urlId);
    }

    // 2. Check pathname (e.g., /products/SKU123)
    const pathMatch = window.location.pathname.match(
      /\/(?:product|item|post|article)s?\/([^\/\?#]+)/i,
    );
    if (pathMatch && pathMatch[1] && isValidContentId(pathMatch[1])) {
      return sanitizeContentId(pathMatch[1]);
    }

    // 3. Check meta tags (safe - HTML escape handled by browser)
    const metaContentId = document.querySelector(
      'meta[name="content-id"], meta[property="product:id"]',
    );
    if (metaContentId) {
      const content = metaContentId.getAttribute("content");
      if (content && isValidContentId(content)) {
        return sanitizeContentId(content);
      }
    }

    // 4. Check data attributes on main/body (safe - getAttribute is XSS-safe)
    const mainElement = document.querySelector("main") || document.body;
    if (mainElement) {
      const dataId =
        mainElement.getAttribute("data-content-id") ||
        mainElement.getAttribute("data-product-id") ||
        mainElement.getAttribute("data-item-id");
      if (dataId && isValidContentId(dataId)) {
        return sanitizeContentId(dataId);
      }
    }

    return "";
  } catch (err) {
    Logger.warn("Failed to extract content ID", err);
    return "";
  }
}

export function initEngagementTracking(): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  if (isTrackingEngagement) {
    return; // Already initialized
  }

  try {
    pageLoadTime = Date.now();
    scrollDepth = 0;
    isTrackingEngagement = true;

    // Track scroll depth (passive listener for performance)
    const updateScrollDepth = () => {
      try {
        const winHeight = window.innerHeight;
        const docHeight = document.documentElement.scrollHeight;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if (docHeight > winHeight) {
          const depth = Math.round(((scrollTop + winHeight) / docHeight) * 100);
          scrollDepth = Math.max(scrollDepth, Math.min(depth, 100)); // Cap at 100%
        } else {
          scrollDepth = 100; // Page fits in viewport
        }
      } catch {
        // Silently fail
      }
    };

    // Initial calculation
    updateScrollDepth();

    // Update on scroll (throttled via passive listener)
    window.addEventListener("scroll", updateScrollDepth, { passive: true });

    // Update on resize (passive listener)
    window.addEventListener("resize", updateScrollDepth, { passive: true });
  } catch (err) {
    Logger.warn("Failed to initialize engagement tracking", err);
  }
}

export function resetEngagementMetrics(): void {
  pageLoadTime = Date.now();
  scrollDepth = 0;
}

export function getEngagementMetrics(): { scroll_depth: number; time_on_page: number } {
  if (!isTrackingEngagement) {
    return { scroll_depth: 0, time_on_page: 0 };
  }

  try {
    const timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000); // seconds
    return { scroll_depth: scrollDepth, time_on_page: timeOnPage };
  } catch (err) {
    Logger.warn("Failed to get engagement metrics", err);
    return { scroll_depth: 0, time_on_page: 0 };
  }
}

export function getUserAgent(): string {
  if (typeof navigator === "undefined" || !navigator.userAgent) {
    return "";
  }

  try {
    const ua = navigator.userAgent;

    // Validate and sanitize
    if (typeof ua !== "string") {
      return "";
    }

    // Length check (user agents are typically 100-500 chars)
    if (ua.length > 1000) {
      Logger.warn("User agent too long, truncating", { length: ua.length });
      return ua.substring(0, 1000);
    }

    if (ua.length === 0) {
      return "";
    }

    // User agent is safe - it's a read-only browser property
    // No need to sanitize further as it can't contain XSS
    return ua;
  } catch (err) {
    Logger.warn("Failed to get user agent", err);
    return "";
  }
}

export function getUtmParams(): {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
} {
  if (typeof window === "undefined") {
    return {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
    };
  }
  const params = new URLSearchParams(window.location.search);
  const clean = (v: string | null): string | null => {
    if (!v) return null;
    const trimmed = v.trim().replace(/<[^>]*>/g, "");
    return trimmed.length > 0 && trimmed.length <= 256 ? trimmed : null;
  };
  return {
    utm_source: clean(params.get("utm_source")),
    utm_medium: clean(params.get("utm_medium")),
    utm_campaign: clean(params.get("utm_campaign")),
    utm_content: clean(params.get("utm_content")),
    utm_term: clean(params.get("utm_term")),
  };
}

export function getAutoTagParams(): {
  gclid: string | null;
  fbclid: string | null;
  msclkid: string | null;
} {
  if (typeof window === "undefined") {
    return { gclid: null, fbclid: null, msclkid: null };
  }
  const params = new URLSearchParams(window.location.search);
  const clean = (v: string | null): string | null => {
    if (!v) return null;
    const trimmed = v.trim();
    return trimmed.length > 0 && trimmed.length <= 256 ? trimmed : null;
  };
  return {
    gclid: clean(params.get("gclid")),
    fbclid: clean(params.get("fbclid")),
    msclkid: clean(params.get("msclkid")),
  };
}

export function buildPageviewPayload(
  cookieToken: string,
  sessionId: string,
  locationId?: string | null,
  locationName?: string | null,
): TrackPageviewRequest {
  const engagement = getEngagementMetrics();
  const utmParams = getUtmParams();
  const autoTagParams = getAutoTagParams();

  const payload: TrackPageviewRequest = {
    cookie_token: cookieToken,
    session_id: sessionId,
    page_url: getPageUrl(),
    page_title: getPageTitle(),
    referrer: getReferrer(),
    page_type: getPageType(),
    timestamp: getTimestamp(),
    user_ip: getUserIp(),
    pageview_id: getPageviewId(),
    traffic_source: getTrafficSource(),
    device_category: getDeviceCategory(),
    content_id: getContentId(),
    scroll_depth: engagement.scroll_depth,
    time_on_page: engagement.time_on_page,
    user_agent: getUserAgent(),
    utm_source: utmParams.utm_source,
    utm_medium: utmParams.utm_medium,
    utm_campaign: utmParams.utm_campaign,
    utm_content: utmParams.utm_content,
    utm_term: utmParams.utm_term,
    gclid: autoTagParams.gclid,
    fbclid: autoTagParams.fbclid,
    msclkid: autoTagParams.msclkid,
  };
  if (locationId) payload.location_id = locationId;
  if (locationName) payload.location_name = locationName;
  return payload;
}

export function validatePageviewPayload(payload: TrackPageviewRequest): boolean {
  if (!payload.session_id) {
    Logger.warn("Invalid pageview payload: missing session_id");
    return false;
  }

  if (!payload.page_url) {
    Logger.warn("Invalid pageview payload: missing page_url");
    return false;
  }

  if (!payload.timestamp) {
    Logger.warn("Invalid pageview payload: missing timestamp");
    return false;
  }

  if (!["desktop", "mobile", "tablet"].includes(payload.device_category)) {
    Logger.warn("Invalid pageview payload: invalid device_category", {
      value: payload.device_category,
    });
    return false;
  }

  return true;
}
